import { useState, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { InvestmentTransaction, InvestmentTransactionList } from '../types/investmentApi.types'
import BackButton from '../../../components/ui-components/BackButton'
import EndOfList from '../components/investment/transactions/EndOfList'
import TransactionRowV2 from '../components/investment/transactions/TransactionRowV2'
import TransactionsSkeletonV2 from '../../../components/skeletons/TransactionsSkeletonV2'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { mfLogoBaseUrl } from '../../../env'
import { getBottomPadding, getTopPadding } from '../../../utils/sessionStorage'
import { fetchInvsetmentTransactions } from '../repository/userInvestmentApi'

export default function InvestmentTransactionsPage() {
  const [transactions, setTransactions] = useState<InvestmentTransaction[]>([])
  const [totalSize, setTotalSize] = useState(0)
  const [fundName, setFundName] = useState('')
  const { isin } = useParams<{ isin: string }>()
  const [searchParams] = useSearchParams()
  const temporaryFamilyAccessToken = searchParams?.get('temporaryFamilyAccessToken') ?? ''
  const observer = useRef<any>()
  const navigate = useNavigate()
  const calledTimeStamps = useRef<number[]>([])
  const { data: metadata, loading: isMetadataLoading } = useMetaDataContext()

  // prefetch metadata fund logo
  useEffect(() => {
    if (metadata?.fundLogo) {
      const img = new Image()
      img.src = `${mfLogoBaseUrl}${metadata?.fundLogo}`
    }
  }, [metadata?.fundLogo])

  // @ts-ignore
  const { mutate, isPending } = useMutation<any, Error>({
    mutationKey: ['investmentTransaction'],
    mutationFn: (timestamp) => {
      // @ts-ignore
      return fetchInvsetmentTransactions(isin, 20, timestamp, temporaryFamilyAccessToken || undefined)
    },
    onSuccess: (data) => {
      const newTransactions = data?.data as InvestmentTransactionList
      setTotalSize(() => newTransactions?.itemsCount)
      setTransactions((transactionsPrev) => {
        return [...transactionsPrev, ...newTransactions?.userTxnResponseList]
      })
    }
  })

  useEffect(() => {
    // @ts-ignore
    mutate(Date.now())
  }, [])

  useEffect(() => {
    setFundName(() => searchParams?.get('fundName') ?? '')
  }, [searchParams])

  useEffect(() => {
    if (transactions.length > 0) {
      const txnDetailListElements = document.querySelectorAll('.txn-detail')
      const lastIndex = txnDetailListElements.length - 1
      const lastChild = txnDetailListElements[lastIndex]

      if (lastChild) {
        observer.current = new IntersectionObserver(async (entries) => {
          const observeOnList = entries[0]
          if (observeOnList.isIntersecting) {
            if (!isPending) {
              const offsetTimeStamp = transactions[transactions.length - 1].timestamp
              if (transactions?.length < totalSize && !calledTimeStamps?.current?.includes(offsetTimeStamp)) {
                calledTimeStamps?.current?.push(offsetTimeStamp)
                // @ts-ignore
                await mutate(offsetTimeStamp)
              }
            }
          }
        })

        observer.current.observe(lastChild)
      }
    }
  }, [transactions])

  return (
    <div className='flex flex-col h-screen'>
      <div
        style={{ paddingTop: `calc(${getTopPadding()}px + 10px)` }}
        className='pb-[18px] px-5 flex gap-6 items-center border-b border-[#F0F4F7]'
      >
        <div>
          <BackButton
            size={24}
            onClick={() => {
              navigate(-1)
            }}
          />
        </div>
        <div>
          <div className='text-[#000000] font-[500]'>Transactions</div>
        </div>
      </div>

      <div
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
        className='flex flex-col flex-1 overflow-y-scroll no-scrollbar'
      >
        <ul
          role='list'
          aria-label='Transaction list'
          className='flex flex-col flex-1 list-none m-0 pl-5 pt-4'
        >
          {transactions?.map((transaction, idx) => {
            return (
              <li
                key={idx.toString(2)}
                role='listitem'
                className='w-full txn-detail animate-fade-150 list-none'
              >
                {metadata ? (
                  <TransactionRowV2
                    transaction={transaction}
                    isFirst={idx === 0}
                    isLast={idx === transactions.length - 1}
                    fundLogo={metadata?.fundLogo ?? ''}
                    fundName={metadata?.fundDetails?.fundName ?? fundName ?? ''}
                  />
                ) : (
                  <TransactionsSkeletonV2 rows={1} />
                )}
              </li>
            )
          })}
          {(isPending || isMetadataLoading || !metadata) && (
            <li className='list-none' aria-hidden='true'>
              <TransactionsSkeletonV2 rows={20} />
            </li>
          )}
        </ul>
        {transactions?.length >= totalSize && !isPending && (
          <div className='flex w-full pt-2 pl-5 pr-5'>
            <EndOfList />
          </div>
        )}
      </div>
    </div>
  )
}
